/* eslint-disable react/destructuring-assignment */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import OnClickToolButton from 'app/components/ToolsBar/OnClickToolButton';
import ToolWithOptions from 'app/components/ToolsBar/ToolWithOptions';
import { useDispatch, useSelector } from 'react-redux';
import getMonitorIdentifier from 'app/utils/screen/getMonitorIdentifier';
import { DisplayableImageData, Viewport } from 'app/interfaces/Image';
import { RealSizeMeasurementCalibrationMeasurement } from 'app/CornerstoneTools/RealSizeMeasurementCalibrationTool';
import getScreenPixelSpacingFromDimensions from 'app/utils/screen/getScreenPixelSpacingFromDimensions';
import MonitorCalibrationModal from 'app/components/MonitorCalibrationModal';
import {
  addMonitorDimensions,
  selectViewerConfiguration,
} from 'app/redux/viewerConfiguration/reducer';

type FullSizeableImage = {
  displayableImage?: DisplayableImageData;
  annotations?: {
    RealSizeMeasurementCalibration?: Record<string, RealSizeMeasurementCalibrationMeasurement>;
  };
  viewport?: Viewport;
  isRealSizeMeasurementCalibration?: boolean;
};

type FullSizeConfigurationButtonProps = {
  applyFullSize: (scale: number) => void;
  viewerImage: FullSizeableImage;
  triggerRealSizeMeasurementCalibration: (onSuccess: () => void) => void;
  hidden?: boolean;
};

export default function FullSizeConfigurationButton({
  viewerImage,
  applyFullSize,
  triggerRealSizeMeasurementCalibration,
  hidden,
}: FullSizeConfigurationButtonProps) {
  const intl = useIntl();
  const { monitorsDimensions } = useSelector(selectViewerConfiguration);
  const dispatch = useDispatch();
  const [calibrationKey, setCalibrationKey] = useState(0);
  const [monitorPixelSpacing, setMonitorPixelSpacing] = useState(undefined);
  const [isMonitorCalibrationOngoing, setIsMonitorCalibrationOngoing] = useState(false);
  // This flag is there to trigger real size measurement calibration once the monitor calibration is done
  const [isFullSizeConfigurationRequested, setIsFullSizeConfigurationRequested] = useState(false);
  const onFullSizeClickRef = React.useRef<() => Promise<void>>();

  const initial10CmLineLength = useMemo(() => {
    if (!monitorPixelSpacing) return undefined;

    return Math.round(
      100 / // 10 cm in mm
        Math.max(monitorPixelSpacing.columnPixelSpacing, monitorPixelSpacing.rowPixelSpacing)
    );
  }, [monitorPixelSpacing]);

  const fullSizeScale = useMemo(() => {
    const realSizeMeasurement = Object.values(
      viewerImage?.annotations?.RealSizeMeasurementCalibration ?? {}
    )?.[0];
    if (viewerImage?.isRealSizeMeasurementCalibration) return undefined;
    if (!monitorPixelSpacing) return undefined;
    if (!realSizeMeasurement) return undefined;
    if (!viewerImage?.displayableImage) return undefined;

    const detectorRealPixelSpacing = {
      columnPixelSpacing: viewerImage.displayableImage.columnPixelSpacing,
      rowPixelSpacing: viewerImage.displayableImage.rowPixelSpacing,
    };

    // cornerstone automatically rescale non 1/1 pixel ratio by matching the smallest to the largest
    const fullSizeScale =
      Math.max(
        detectorRealPixelSpacing.columnPixelSpacing,
        detectorRealPixelSpacing.rowPixelSpacing
      ) / Math.max(monitorPixelSpacing.columnPixelSpacing, monitorPixelSpacing.rowPixelSpacing);

    return fullSizeScale;
  }, [
    monitorPixelSpacing,
    viewerImage?.displayableImage,
    viewerImage?.isRealSizeMeasurementCalibration,
    viewerImage?.annotations?.RealSizeMeasurementCalibration,
  ]);

  const isFullSizeScaleForViewport =
    fullSizeScale !== undefined && Math.abs(fullSizeScale - viewerImage?.viewport?.scale) < 0.005;

  const onMonitorCalibrationSuccess = useCallback<
    React.ComponentProps<typeof MonitorCalibrationModal>['onSuccess']
  >(async (monitorDimensions) => {
    const monitorIdentifier = await getMonitorIdentifier();

    dispatch(addMonitorDimensions({ monitorIdentifier, monitorDimensions }));
    setIsMonitorCalibrationOngoing(false);
    onFullSizeClickRef.current();
  }, []);

  const stopIsMonitorCalibrationOngoing = useCallback(() => {
    setIsFullSizeConfigurationRequested(false);
    setIsMonitorCalibrationOngoing(false);
    setCalibrationKey((prevKey) => prevKey + 1);
  }, []);

  onFullSizeClickRef.current = async () => {
    const monitorIdentifier = await getMonitorIdentifier();
    const monitorPixelSpacing = getScreenPixelSpacingFromDimensions(
      monitorsDimensions?.[monitorIdentifier]
    );
    if (!monitorPixelSpacing) {
      setIsMonitorCalibrationOngoing(true);
      setIsFullSizeConfigurationRequested(true);
      return;
    }
    const realSizeMeasurement = Object.values(
      viewerImage.annotations?.RealSizeMeasurementCalibration ?? {}
    )[0];
    if (!realSizeMeasurement) {
      triggerRealSizeMeasurementCalibration(() => onFullSizeClickRef.current());
      return;
    }
    applyFullSize(fullSizeScale);
  };

  // Trigger RealSizeMeasurement after monitor calibration
  useEffect(() => {
    if (isFullSizeConfigurationRequested && !isMonitorCalibrationOngoing) {
      triggerRealSizeMeasurementCalibration(() => onFullSizeClickRef.current());
      setIsFullSizeConfigurationRequested(false);
    }
  }, [
    isFullSizeConfigurationRequested,
    isMonitorCalibrationOngoing,
    triggerRealSizeMeasurementCalibration,
  ]);

  useEffect(() => {
    if (!monitorsDimensions) return;
    (async () => {
      try {
        const monitorIdentifier = await getMonitorIdentifier();
        const monitorDimensions = monitorsDimensions?.[monitorIdentifier];
        if (!monitorDimensions) return;

        const monitorPixelSpacing = getScreenPixelSpacingFromDimensions(
          monitorsDimensions?.[monitorIdentifier]
        );
        setMonitorPixelSpacing(monitorPixelSpacing);
      } catch {}
    })();
  }, [
    monitorsDimensions,
    window.screen.width,
    window.screen.height,
    // We use below value ot detect monitor changes
    window.screenTop,
    window.screenLeft,
  ]);

  const actions = [
    {
      content: intl.formatMessage({ id: 'tools.full_size.start_configuration' }),
      action: () => {
        setIsMonitorCalibrationOngoing(true);
        setIsFullSizeConfigurationRequested(true);
      },
    },
  ];

  if (hidden) return null;
  return (
    <>
      <ToolWithOptions
        content={
          <OnClickToolButton
            aria-label="viewer-action"
            onClick={onFullSizeClickRef.current}
            content={<i className="svg-icon full-size" />}
            tooltipId="tools.full_size.tooltip"
            isActive={isFullSizeScaleForViewport}
          />
        }
        actions={actions}
      />
      <MonitorCalibrationModal
        key={calibrationKey}
        onSuccess={onMonitorCalibrationSuccess}
        onCancel={stopIsMonitorCalibrationOngoing}
        open={isMonitorCalibrationOngoing}
        onClose={stopIsMonitorCalibrationOngoing}
        initialLineLength={initial10CmLineLength}
      />
    </>
  );
}
